import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { TeamLogo } from "@/components/TeamLogo";
import { LeagueLogo } from "@/components/LeagueLogo";
import { Circle, Calendar, RefreshCw, Loader2 } from "lucide-react";

const LEAGUES = ["All", "NBA", "NFL", "NHL", "MLB", "NCAAF", "NCAAB", "EPL", "MMA"];
const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "live", label: "Live" },
  { id: "final", label: "Final" },
  { id: "scheduled", label: "Upcoming" },
];

function buildDateOptions() {
  const opts = [];
  const today = new Date();
  for (let i = -2; i <= 2; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const label = i === 0 ? "Today" : i === -1 ? "Yesterday" : i === 1 ? "Tomorrow" : d.toLocaleDateString([], { month: "short", day: "numeric" });
    opts.push({ iso, label });
  }
  return opts;
}

export default function Scores() {
  const dateOpts = useMemo(buildDateOptions, []);
  const [date, setDate] = useState(dateOpts.find((d) => d.label === "Today").iso);
  const [league, setLeague] = useState("All");
  const [status, setStatus] = useState("all");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { date, status };
    if (league !== "All") params.league = league;
    api.get("/scores", { params })
      .then((r) => setGames(r.data.games))
      .finally(() => setLoading(false));
  };

  useEffect(load, [date, league, status]);

  // Auto-refresh live scores every 60s
  useEffect(() => {
    if (status !== "live" && status !== "all") return;
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, league, status]);

  const grouped = useMemo(() => {
    const m = {};
    for (const g of games) {
      if (!m[g.league]) m[g.league] = [];
      m[g.league].push(g);
    }
    return m;
  }, [games]);

  const liveCount = games.filter((g) => g.status?.short === "LIVE").length;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="scores-page">
      <PageHeader
        title="Scoreboard"
        subtitle="Live, final, and upcoming games from The Odds API. Live scores auto-refresh every 60s."
        actions={
          <button onClick={load} className="iso-btn-outline !py-1.5 !text-xs" data-testid="scores-refresh-btn">
            {loading ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            Refresh
          </button>
        }
      />

      {/* Date selector */}
      <div className="iso-card flex flex-wrap items-center gap-1 p-1.5">
        <div className="flex items-center gap-1.5 px-3 text-xs font-semibold text-gray-500">
          <Calendar className="size-3.5" /> Date
        </div>
        {dateOpts.map((d) => (
          <button
            key={d.iso}
            onClick={() => setDate(d.iso)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
              date === d.iso ? "bg-primary text-primary-foreground" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            }`}
            data-testid={`scores-date-${d.label.toLowerCase()}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* League + status filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-gray-0 p-1 overflow-x-auto no-scrollbar">
          {LEAGUES.map((l) => (
            <button
              key={l}
              onClick={() => setLeague(l)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all whitespace-nowrap ${
                league === l ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
              }`}
              data-testid={`scores-league-${l.toLowerCase()}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-gray-0 p-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatus(t.id)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                status === t.id ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
              }`}
              data-testid={`scores-status-${t.id}`}
            >
              {t.id === "live" && liveCount > 0 && (
                <span className="relative flex size-1.5">
                  <span className="absolute size-full animate-ping rounded-full bg-red opacity-75" />
                  <span className="relative size-1.5 rounded-full bg-red" />
                </span>
              )}
              {t.label}
              {t.id === "live" && liveCount > 0 && (
                <span className="font-lexend text-[10px] opacity-70">{liveCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Games */}
      {loading && games.length === 0 ? (
        <div className="iso-card p-12 text-center text-sm text-gray-500">
          <Loader2 className="mx-auto mb-2 size-5 animate-spin" />
          Loading scoreboard…
        </div>
      ) : games.length === 0 ? (
        <div className="iso-card p-12 text-center">
          <Circle className="mx-auto size-8 text-gray-300" />
          <h3 className="mt-3 font-lexend">No games found</h3>
          <p className="mt-1 text-sm text-gray-500">Try a different date or league. The Odds API returns games within a 4-day rolling window.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([lg, items]) => (
          <div key={lg} className="space-y-3" data-testid={`scores-group-${lg}`}>
            <div className="flex items-center gap-2">
              <LeagueLogo code={lg} size={22} />
              <div className="font-lexend text-sm font-bold text-gray-900">{lg}</div>
              <span className="iso-chip">{items.length} games</span>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {items.map((g) => <ScoreCard key={g.id} game={g} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const ScoreCard = ({ game }) => {
  const st = game.status?.short || "SCHEDULED";
  const live = st === "LIVE";
  const final = st === "FINAL";
  const scheduled = st === "SCHEDULED";

  const aw = game.away;
  const hm = game.home;
  const awWin = final && aw.score > hm.score;
  const hmWin = final && hm.score > aw.score;

  return (
    <div className="iso-card p-4" data-testid={`score-card-${game.id}`}>
      <div className="flex items-center justify-between">
        <StatusPill status={st} />
        <div className="text-[10px] font-medium text-gray-400">
          {new Date(game.scheduled_iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        </div>
      </div>
      <div className="mt-3 space-y-2">
        <TeamScoreRow team={aw} winner={awWin} dimmed={hmWin} scheduled={scheduled} />
        <TeamScoreRow team={hm} winner={hmWin} dimmed={awWin} scheduled={scheduled} home />
      </div>
    </div>
  );
};

const TeamScoreRow = ({ team, winner, dimmed, scheduled, home }) => (
  <div className={`flex items-center justify-between ${dimmed ? "opacity-60" : ""}`}>
    <div className="flex items-center gap-3 min-w-0">
      <TeamLogo src={team.logo} abbr={abbrFor(team.name)} size={34} />
      <div className="min-w-0">
        <div className={`truncate text-sm ${winner ? "font-bold text-gray-900" : "font-semibold text-gray-700"}`}>
          {team.name}
        </div>
        {home && <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Home</div>}
      </div>
    </div>
    <div className={`font-lexend tabular-nums ${scheduled ? "text-xs text-gray-300" : winner ? "text-2xl font-bold text-gray-900" : "text-2xl font-bold text-gray-500"}`}>
      {scheduled ? "—" : team.score ?? "0"}
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  if (status === "LIVE") {
    return (
      <span className="iso-chip iso-chip-red">
        <span className="relative flex size-1.5">
          <span className="absolute size-full animate-ping rounded-full bg-red opacity-75" />
          <span className="relative size-1.5 rounded-full bg-red" />
        </span>
        LIVE
      </span>
    );
  }
  if (status === "FINAL") {
    return <span className="iso-chip">FINAL</span>;
  }
  if (status === "SCHEDULED") {
    return <span className="iso-chip iso-chip-blue">UPCOMING</span>;
  }
  return <span className="iso-chip">{status}</span>;
};

function abbrFor(name) {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 3).toUpperCase();
}
